import { sha256 } from '../crypto/sha256';
import { importSpki, type PublicKey } from '../crypto/sig';
import { leafHash } from '../merkle/hash';
import { encodeMerkleTreeLeaf } from '../rfc6962/leaf';
import { verifySct } from '../rfc6962/sct';
import type { Sct, SignedEntry, TimestampedEntry } from '../rfc6962/types';
import { OID, parseCertificate, type ParsedCert } from '../x509/parse';
import { issuerKeyHash } from '../x509/spki';
import { extractTbs, removeExtension } from '../x509/tbs';

export type SctVerdict = { ok: true } | { ok: false; error?: string } | { ok: 'unverifiable'; reason: string };

export interface EmbeddedSctContext {
  /** The leaf certificate (final cert or precert) as parsed. */
  leaf: ParsedCert;
  /** The `signed_entry` the SCTs cover, when it could be reconstructed. */
  entry?: SignedEntry;
  /** Why `entry` could not be built. */
  problem?: string;
}

/**
 * Reconstructs the precert `signed_entry` that SCTs embedded in a certificate were issued over:
 * TBS minus the SCT list (final cert) or minus the poison (precert), plus SHA-256 of the CA's SPKI.
 * `issuers` is the chain above the leaf, nearest first.
 */
export async function embeddedSctEntry(leafDer: Uint8Array, issuers: Uint8Array[]): Promise<EmbeddedSctContext> {
  const leaf = parseCertificate(leafDer);
  const ctx: EmbeddedSctContext = { leaf };
  if (leaf.scts.length === 0 && !leaf.hasPoison) {
    ctx.problem = 'The certificate has no embedded SCTs.';
    return ctx;
  }
  const first = issuers[0];
  if (!first) {
    ctx.problem = 'The issuer certificate is needed to compute issuer_key_hash. Paste the CA certificate after the leaf.';
    return ctx;
  }
  let issuer: ParsedCert;
  try {
    issuer = parseCertificate(first);
  } catch (e) {
    ctx.problem = `The issuer certificate could not be parsed: ${e instanceof Error ? e.message : String(e)}`;
    return ctx;
  }
  let caDer = first;
  let tbs = extractTbs(leafDer);
  if (leaf.hasPoison) {
    // A precertificate: SCTs (if any are embedded, unusual) cover the poison-free TBS.
    tbs = removeExtension(tbs, OID.ctPoison);
    if (issuer.isPrecertSigningCert) {
      const ca = issuers[1];
      if (!ca) {
        ctx.problem = 'The precert was signed by a Precertificate Signing Certificate; the CA certificate above it is also required.';
        return ctx;
      }
      // RFC 6962 §3.2 additionally rewrites Issuer and AKI in the TBS; not implemented.
      ctx.problem = 'Precertificates signed by a Precertificate Signing Certificate need their Issuer/AKI rewritten before verification; this explorer does not do that yet.';
      caDer = ca;
      return ctx;
    }
  } else {
    tbs = removeExtension(tbs, OID.sctList);
    if (issuer.isPrecertSigningCert) {
      ctx.problem = 'The certificate directly above a final certificate cannot be a Precertificate Signing Certificate; check the chain order.';
      return ctx;
    }
  }
  ctx.entry = { type: 'precert', issuerKeyHash: await issuerKeyHash(caDer), tbs };
  return ctx;
}

export interface LogKeyLookup {
  (logId: Uint8Array): Uint8Array | undefined;
}

const keyCache = new Map<string, Promise<PublicKey>>();
function keyFor(spki: Uint8Array): Promise<PublicKey> {
  const k = spki.toHex();
  let p = keyCache.get(k);
  if (!p) {
    p = importSpki(spki);
    keyCache.set(k, p);
  }
  return p;
}

/** Verifies each SCT against the log key found by `lookup`; unknown logs are reported, not failed. */
export async function verifyScts(scts: Sct[], entry: SignedEntry | undefined, lookup: LogKeyLookup): Promise<SctVerdict[]> {
  return Promise.all(
    scts.map(async (sct): Promise<SctVerdict> => {
      if (!entry) return { ok: 'unverifiable', reason: 'signed entry could not be reconstructed' };
      const spki = lookup(sct.logId);
      if (!spki) return { ok: 'unverifiable', reason: 'log not in the log list — no public key' };
      if (sct.version !== 0) return { ok: false, error: `unsupported SCT version ${sct.version}` };
      const r = await verifySct(sct, entry, await keyFor(spki));
      return r.ok ? { ok: true } : { ok: false, ...(r.error ? { error: r.error } : {}) };
    }),
  );
}

/** The MerkleTreeLeaf a log would have created for this SCT, and its hash — for inclusion lookups. */
export async function leafForSct(sct: Sct, entry: SignedEntry): Promise<{ leaf: Uint8Array; hash: Uint8Array }> {
  const te: TimestampedEntry = { timestamp: sct.timestamp, entryType: entry.type, extensions: sct.extensions };
  if (entry.type === 'x509') te.x509 = entry.cert;
  else te.precert = { issuerKeyHash: entry.issuerKeyHash, tbs: entry.tbs };
  const leaf = encodeMerkleTreeLeaf(te);
  return { leaf, hash: await leafHash(leaf) };
}

export { sha256 };
